import React, { useState } from 'react';
import { Button, Space, Typography, message } from 'antd';
import { CopyOutlined, ExpandOutlined, CompressOutlined } from '@ant-design/icons';
import { copyToClipboard } from '../utils';
import './JsonView.css';

const JsonView = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  
  const handleCopyJson = () => {
    const jsonString = JSON.stringify(data, null, 2);
    copyToClipboard(jsonString);
    message.success('JSON copied to clipboard');
  };
  
  const formatJson = (obj, depth = 0) => {
    if (obj === null) return <span className="json-null">null</span>;
    if (typeof obj === 'undefined') return <span className="json-null">undefined</span>;
    if (typeof obj === 'boolean') return <span className="json-boolean">{obj.toString()}</span>;
    if (typeof obj === 'number') return <span className="json-number">{obj}</span>;
    if (typeof obj === 'string') {
      // Check if it's a URL
      if (obj.startsWith('http://') || obj.startsWith('https://')) {
        return (
          <span className="json-string">
            "<a href={obj} target="_blank" rel="noopener noreferrer" className="json-url">{obj}</a>"
          </span>
        );
      }
      return <span className="json-string">"{obj}"</span>;
    }
    
    if (Array.isArray(obj)) {
      if (!expanded && depth > 1 && obj.length > 3) {
        return <span className="json-collapsed">[{obj.length} items]</span>;
      }
      
      return (
        <span>
          [
          {obj.length > 0 && (
            <div className="json-indent">
              {obj.map((item, index) => (
                <div key={index}>
                  {formatJson(item, depth + 1)}
                  {index < obj.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          ]
        </span>
      );
    }
    
    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      
      if (!expanded && depth > 1 && entries.length > 5) {
        return <span className="json-collapsed">{`{${entries.length} properties}`}</span>;
      }
      
      return (
        <span>
          {'{'}
          {entries.length > 0 && (
            <div className="json-indent">
              {entries.map(([key, value], index) => (
                <div key={key}>
                  <span className="json-key">"{key}"</span>: {formatJson(value, depth + 1)}
                  {index < entries.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          {'}'}
        </span>
      );
    }
    
    return <span>{JSON.stringify(obj)}</span>;
  };
  
  return (
    <div className="json-view">
      <div className="json-view-header">
        <Typography.Title level={4} className="json-title">
          Raw JSON Data
        </Typography.Title>
        <Space>
          <Button
            icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Collapse' : 'Expand'} All
          </Button>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={handleCopyJson}
          >
            Copy JSON
          </Button>
        </Space>
      </div>
      
      <div className="json-content">
        <pre className="json-pre">
          {formatJson(data)}
        </pre>
      </div>
    </div>
  );
};

export default JsonView;
